import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import Category from "../home/Category";
import useTochiLookup from "./useTochiLookup";

// 玄関（/）の段「歴史と人と土地をつなぐ」。
//   説明 → 「宝蔵を開く（フリー）」 → 押してから位置を聞く → 町字に頁があれば、その場に土地の頁をすべて（TochiGlimpse。/tochi と同じ中身）。
//   頁が無い・位置を使えないときは、その旨を一行で。index.json と村の頁は押してから読む。
//   開くときの所作: 読み込み中は段の高さを保ち（ページが縮んでスクロールが上へ飛ばないように）、
//   頁がひらいたら段の頭へ静かに寄せ、各段は視界に入った順に灯る（Reveal）。
// 言葉の約束: 人と人の縁はここでは語らない（「出会い」「マッチング」「袖振り合う」を出さない）。

const TochiGlimpse = lazy(() => import("./TochiGlimpse"));

const SERIF = '"Hiragino Mincho ProN", "Yu Mincho", serif';
const WASHI = "#ece5d4";
const WASHI_DIM = "rgba(222,215,198,0.6)";
const WASHI_FAINT = "rgba(222,215,198,0.42)";
const GOLD = "#d4b46e";
const GOLD_TEXT = "#e6d3a3";

const DOOR_SX = {
  fontFamily: SERIF,
  fontWeight: 600,
  px: { xs: 4, sm: 6 },
  py: 1.5,
  fontSize: "0.95rem",
  letterSpacing: 4,
  textIndent: 4,
  whiteSpace: "nowrap",
  color: GOLD_TEXT,
  bgcolor: "rgba(212,180,110,0.05)",
  border: "1px solid rgba(212,180,110,0.5)",
  borderRadius: 0,
  boxShadow: "0 0 24px rgba(212,180,110,0.1)",
  transition: "all 0.3s ease",
  "&:hover": { bgcolor: "rgba(212,180,110,0.12)", borderColor: "rgba(224,201,143,0.9)", color: "#f2e5c0" },
  "&.Mui-disabled": { color: "rgba(230,211,163,0.5)", borderColor: "rgba(212,180,110,0.28)", boxShadow: "none" },
};
const QUIET_SX = {
  color: WASHI_DIM,
  fontSize: "0.8rem",
  fontFamily: SERIF,
  letterSpacing: 2,
  "&:hover": { color: WASHI, bgcolor: "rgba(255,255,255,0.04)" },
};

export const TOCHI_TITLE = "歴史と人と土地をつなぐ";
// 改行はスマホ幅（約22字／行）で折り返しが崩れない位置に置いてある
export const TOCHI_BODY =
  "いま立っているその土地は、\n江戸のころ、何という村だったか。\n風土記稿など、そのころの記録から、\nこの土地で起きたことを引いて見せる。";
export const TOCHI_NOTE =
  "いまの場所を知るために、端末の位置情報を使います。\n位置は町字を調べるのに使うだけで、保存しません。";
export const TOCHI_DOOR = "宝蔵を開く（フリー）";

const REASON = {
  none: "いまの場所の頁は、まだひらいていない。",
  denied: "位置を使えなかったので、いま立っている場所が分からない。",
  error: "いまの場所を調べられなかった。しばらくして、もう一度。",
};

// 固定ヘッダーの下に段の頭が来るように
const SCROLL_MARGIN = 72;

export default function TochiCategory() {
  const look = useTochiLookup();
  const [opened, setOpened] = useState(false);
  // 読み込み中に保つ高さ（押す直前の段ぜんぶ）。頁がひらいたら解く。
  // 説明と扉が消えて段が縮むと、ページが短くなってスクロール位置が上へ押し戻されるため
  const [hold, setHold] = useState(0);
  const sectionRef = useRef(null);
  const scrollOnClose = useRef(false);

  const press = async () => {
    const h = sectionRef.current?.getBoundingClientRect().height || 0;
    const r = await look.run();
    if (r.status === "found") {
      setHold(h);
      setOpened(true);
    }
  };

  // 段の題（h2）が固定ヘッダーの少し下に来る位置
  const titleTop = () => {
    const el = sectionRef.current?.querySelector("h2") || sectionRef.current;
    if (!el || typeof window === "undefined") return null;
    return Math.max(0, el.getBoundingClientRect().top + window.scrollY - SCROLL_MARGIN);
  };

  // 頁がひらいた: 高さの保持を解き、段の題へ静かに寄せる（上に飛ばさない）
  const handleReady = useCallback(() => {
    setHold(0);
    const top = titleTop();
    if (top == null) return;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
  }, []);

  // 閉じる: 頁が畳まれてページが短くなった「あと」で、段の題の位置に合わせる（畳む前に計ると位置がずれる）
  const close = () => {
    scrollOnClose.current = true;
    setOpened(false);
  };
  useEffect(() => {
    if (opened || !scrollOnClose.current) return;
    scrollOnClose.current = false;
    const top = titleTop();
    if (top != null) window.scrollTo({ top, behavior: "auto" });
  }, [opened]);

  const showGlimpse = opened && look.status === "found";

  return (
    <Box ref={sectionRef} sx={{ minHeight: showGlimpse && hold ? hold : undefined }}>
      <Category title={TOCHI_TITLE} body={showGlimpse ? null : TOCHI_BODY} testId="tochi-category">
        {showGlimpse ? (
          <Box sx={{ maxWidth: 440, mx: "auto" }}>
            <Suspense
              fallback={
                <Stack alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress size={22} sx={{ color: GOLD }} />
                </Stack>
              }
            >
              <TochiGlimpse place={look.place} pos={look.pos} onClose={close} onReady={handleReady} />
            </Suspense>
          </Box>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Button size="large" onClick={press} disabled={look.status === "locating"} sx={DOOR_SX}>
              {look.status === "locating" ? "いまの場所を見ている…" : TOCHI_DOOR}
            </Button>
            {REASON[look.status] && (
              <Typography data-testid="tochi-reason" sx={{ fontFamily: SERIF, fontSize: "0.85rem", letterSpacing: 1.2, lineHeight: 2, color: WASHI }}>
                {look.message || REASON[look.status]}
              </Typography>
            )}
            {look.status === "found" && !opened && (
              <Button variant="text" size="small" onClick={() => setOpened(true)} sx={QUIET_SX}>
                もう一度ひらく
              </Button>
            )}
            <Typography
              sx={{
                fontFamily: SERIF,
                fontSize: "0.72rem",
                letterSpacing: 1.2,
                lineHeight: 2,
                whiteSpace: "pre-line",
                color: WASHI_FAINT,
                pt: 1,
              }}
            >
              {TOCHI_NOTE}
            </Typography>
          </Stack>
        )}
      </Category>
    </Box>
  );
}
